import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { page } from '../../../analytics';
import { useLaunchDarkly } from '../../../launchdarkly';
import { PaymentMethodType } from '../../../models';
import {
  fromAccounts,
  fromAuth,
  fromBillingInformation,
  fromPaymentMethod,
  useAppDispatch,
} from '../../../store';
import { PaymentMethodCards } from '../payment-method/PaymentMethodCards';
import {
  InlineCardBillingDetails,
  InlineCardForm,
  InlineCardFormHandle,
} from '../payment-method/inline/InlineCardForm';
import {
  BillingDetailsForm,
  BillingDetailsFormHandle,
  BillingSaveOutcome,
  isBillingInformationComplete,
} from './BillingDetailsForm';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    // Design: Inter for labels/body, Inter Tight only on headings.
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    '& .MuiTypography-root:not(h2), & .MuiInputBase-root, & button': {
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 32,
  },
  paymentHeader: {
    margin: '20px 0 10px',
  },
  billingHeader: {
    marginTop: 20,
  },
  headingGroup: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 12,
    rowGap: 2,
    minWidth: 0,
  },
  heading: {
    margin: 0,
    '&&': {
      fontFamily: "'Inter Tight', sans-serif",
    },
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.3,
    color: '#16141d',
  },
  subheading: {
    margin: 0,
    fontSize: 11,
    lineHeight: 1.4,
    color: '#5b6661',
  },
  actionLink: {
    flex: 'none',
    minHeight: 32,
    padding: '0 4px',
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#0b6b4f',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // Edit mode of a saved section: "Cancel" (ghost) + "Save" (primary), 32px high (design 6.10).
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 'none',
  },
  ghostButton: {
    height: 32,
    minWidth: 72,
    padding: '0 12px',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#0b6b4f',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      backgroundColor: '#f3faf6',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  primaryButton: {
    height: 32,
    minWidth: 64,
    padding: '0 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: '1px solid #0f8461',
    borderRadius: 8,
    backgroundColor: '#0f8461',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#ffffff',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      backgroundColor: '#0b6b4f',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  cardForm: {
    marginTop: 16,
  },
  // Saved bank transfer (design: piSavedIsBank).
  savedBank: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    border: '1px solid #cdd6d2',
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  savedBankIcon: {
    flex: 'none',
    width: 44,
    height: 30,
    border: '1px solid #e1e7e4',
    borderRadius: 4,
    backgroundColor: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#16141d',
  },
  savedBankTitle: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  savedBankHint: {
    display: 'block',
    marginTop: 2,
    fontSize: 12,
    color: '#5b6661',
  },
  defaultBadge: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 8px',
    borderRadius: 999,
    backgroundColor: '#e6f4ee',
    fontSize: 12,
    fontWeight: 600,
    color: '#0b6b4f',
  },
  note: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
    fontSize: 12,
    color: '#5b6661',
  },
  errorText: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.4,
    color: '#d14343',
  },
}));

/** Which saved section(s) hold unsaved edits when Pay now is clicked. */
export type UnsavedEdits = 'payment' | 'billing' | 'both';

export interface SaveEditsOutcome {
  /** Every open edit was persisted (or there was nothing to persist). */
  ok: boolean;
  /** The billing form's outcome when it was saved — the page re-prices from it. */
  billing?: BillingSaveOutcome;
}

export interface PaymentBillingSectionHandle {
  /** Validates (and saves when changed) the billing details. */
  validateAndSaveBillingInformation: () => Promise<BillingSaveOutcome>;
  /** Inline card checkout: entering a new card (vs paying with the saved one)? */
  isEnteringNewCard: () => boolean;
  /** Inline card checkout: tokenise the entered card into a `pm_` id. */
  createInlinePaymentMethod: (billingDetails: InlineCardBillingDetails) => Promise<string>;
  /** A saved section is in edit mode (dirty or not). */
  isEditing: () => boolean;
  /** The saved section(s) in edit mode with unsaved changes, if any. */
  getUnsavedEdits: () => UnsavedEdits | undefined;
  /** Leaves every edit mode and drops the edits (what is on file stays). */
  discardEdits: () => void;
  /** Persists every open edit (payment method first, then billing) and leaves edit mode. */
  saveEdits: () => Promise<SaveEditsOutcome>;
}

interface PaymentBillingSectionProps {
  accountId: number;
  availableRenewal?: boolean;
  hasMonthlyItem?: boolean;
  hasPaidSubscription?: boolean;
  handleSelectPaymentMethod: (paymentMethod: PaymentMethodType) => void;
  /** Inline card checkout flag (`inlineCardCheckoutEnabled`): render the card block. */
  inlineCardEnabled?: boolean;
}

const BankIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10h16M5 10l7-4.5 7 4.5M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/**
 * Left column of the Payment & Billing page (design "Purchase Portal 2 [6.10]", view payinfo,
 * issue #1274 #1/#7):
 *
 * - **Payment method** — with a payment method on file (saved card, or bank transfer as the
 *   account default) the section shows it with an "Edit" link. Edit opens the method cards +
 *   the card elements with "Cancel" / "Save": Save persists the choice (new card tokenised and
 *   attached as the default card through the save-card API, or bank transfer as the default
 *   method) and closes the edit. A new account gets the open cards + elements, no Edit/Save.
 * - **Card information** — the inline Stripe Elements (flag ON, card selected).
 * - **Billing information** — a read-only summary of the saved address with an "Edit" link;
 *   Edit opens the form with "Cancel" / "Save". Save writes the address, which re-prices the
 *   order (tax). A new account (or out-of-sync address) gets the open form, saved by Pay now.
 *
 * Pay now while a saved section is being edited: the page asks through the handle
 * (`getUnsavedEdits` → "Save your changes before paying?" → `saveEdits` / `discardEdits`).
 */
export const PaymentBillingSection = forwardRef<
PaymentBillingSectionHandle,
PaymentBillingSectionProps
>((
  {
    accountId,
    availableRenewal,
    hasMonthlyItem,
    hasPaidSubscription,
    handleSelectPaymentMethod,
    inlineCardEnabled = false,
  },
  ref,
) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { flags } = useLaunchDarkly();
  const cardRef = useRef<InlineCardFormHandle>(null);
  const billingRef = useRef<BillingDetailsFormHandle>(null);

  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const defaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const isPublicDomain = useSelector(fromAccounts.selectIsPublicDomain);
  const savedCard = useSelector(fromPaymentMethod.selectOneByAccountId(Number(accountId)));
  const savedBilling = useSelector(fromBillingInformation.selectOneByAccountId(Number(accountId)));
  const user = useSelector(fromAuth.selectUser);
  const billingNotSynced = useSelector(
    fromBillingInformation.isNotSyncedAddressByAccountId(Number(accountId)),
  ) ?? false;

  const [editingPayment, setEditingPayment] = useState(false);
  const [editingBilling, setEditingBilling] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);

  useEffect(() => {
    page();
  }, []);

  // The saved address decides summary vs form (the legacy section loaded it the same way).
  useEffect(() => {
    if (!accountId) return;
    dispatch(fromBillingInformation.doGetBillingInformation(Number(accountId)))
      .catch(() => {
        // no saved address: the form simply opens
      });
  }, [accountId, dispatch]);

  useEffect(() => {
    if (!defaultPaymentMethod && !savedCard) {
      dispatch(fromPaymentMethod.doSetSelectedPaymentMethod(PaymentMethodType.CREDIT_CARD));
    }
  }, [defaultPaymentMethod, savedCard, dispatch]);

  /**
   * Bank transfer is hidden for: monthly items, accounts with a paid subscription, direct →
   * self-serve switches, flag off, public email domains (same rules as the legacy section).
   */
  const bankTransferUnavailable = defaultPaymentMethod !== PaymentMethodType.AWS_MARKETPLACE
    && (!!hasMonthlyItem || !!hasPaidSubscription || !availableRenewal
      || !flags?.bankTransferEnabled || !!isPublicDomain);

  useEffect(() => {
    if (bankTransferUnavailable) {
      dispatch(fromPaymentMethod.doSetSelectedPaymentMethod(PaymentMethodType.CREDIT_CARD));
    }
  }, [bankTransferUnavailable, dispatch]);

  const isCard = bankTransferUnavailable || selectedPaymentMethod === PaymentMethodType.CREDIT_CARD;

  // An AWS Marketplace account pays through AWS only: the other methods render disabled, and a
  // selection made before the default arrived (or by any other path) is pulled back to AWS so
  // the order never goes out as a card / bank-transfer checkout (Aikido).
  const lockedMethod = defaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE
    ? PaymentMethodType.AWS_MARKETPLACE
    : undefined;
  useEffect(() => {
    if (lockedMethod && selectedPaymentMethod !== lockedMethod) {
      dispatch(fromPaymentMethod.doSetSelectedPaymentMethod(lockedMethod));
    }
  }, [lockedMethod, selectedPaymentMethod, dispatch]);

  // What is on file for this order: bank transfer as the account default (when the order may
  // use it), else the saved card. Nothing on file → the open cards + elements (new account).
  // An AWS Marketplace account is billed through AWS: its saved card is never "on file" for
  // this order, so the method cards (with the AWS option) stay and the selection is untouched.
  const savedKind: 'bank' | 'card' | undefined = (() => {
    if (!inlineCardEnabled) return undefined;
    if (defaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE) return undefined;
    if (defaultPaymentMethod === PaymentMethodType.BANK_TRANSFER && !bankTransferUnavailable) {
      return 'bank';
    }
    return savedCard ? 'card' : undefined;
  })();
  const savedMethod = savedKind === 'bank'
    ? PaymentMethodType.BANK_TRANSFER
    : PaymentMethodType.CREDIT_CARD;
  const showSavedPayment = !!savedKind && !editingPayment;

  // The order goes out with the method on file while it is shown as such.
  useEffect(() => {
    if (showSavedPayment && selectedPaymentMethod !== savedMethod) {
      handleSelectPaymentMethod(savedMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSavedPayment, savedMethod, selectedPaymentMethod]);

  const showCardElements = inlineCardEnabled && isCard && (!savedKind || editingPayment);
  const showSavedCardBlock = showSavedPayment && savedKind === 'card';

  // A saved address missing a required field (e.g. City, collected since design 6.10) is not
  // "on file": the form opens and Pay now validates it, like an out-of-sync one.
  const billingComplete = isBillingInformationComplete(savedBilling, user?.email ?? '');
  const hasSavedBilling = !!savedBilling && !billingNotSynced && billingComplete;
  const showBillingSummary = hasSavedBilling && !editingBilling;

  const handleEditPayment = () => {
    setPaymentError(undefined);
    setEditingPayment(true);
  };
  const handleCancelPayment = () => {
    cardRef.current?.clear();
    setPaymentError(undefined);
    setEditingPayment(false);
    if (selectedPaymentMethod !== savedMethod) handleSelectPaymentMethod(savedMethod);
  };

  /** Persists the method chosen in edit mode; false (with the error shown) when it cannot. */
  const savePayment = async (): Promise<boolean> => {
    setSavingPayment(true);
    setPaymentError(undefined);
    try {
      if (!isCard) {
        if (defaultPaymentMethod !== PaymentMethodType.BANK_TRANSFER) {
          await dispatch(fromPaymentMethod.doUpdateDefaultPaymentMethod({
            defaultPaymentMethod: PaymentMethodType.BANK_TRANSFER,
          })).then(unwrapResult);
        }
      } else {
        const card = cardRef.current;
        if (card?.isDirty()) {
          if (!card.isComplete()) {
            setPaymentError(intl.formatMessage({ id: 'checkout.card.invalid' }));
            return false;
          }
          const stripeToken = await card.createToken();
          // The card list may still be loading (bank default → Edit → Card → Save in one go):
          // ask the BE again before deciding, so an existing card is replaced, never doubled.
          const cardOnFile = savedCard
            ?? (await dispatch(fromPaymentMethod.doGetPaymentMethod(Number(accountId)))
              .then(unwrapResult))
              .find(it => it.accountId === Number(accountId));
          if (cardOnFile) {
            await dispatch(fromPaymentMethod.doUpdatePaymentMethod({
              id: cardOnFile.id,
              stripeToken,
            })).then(unwrapResult);
          } else {
            await dispatch(fromPaymentMethod.doCreatePaymentMethod({
              accountId: Number(accountId),
              stripeToken,
            })).then(unwrapResult);
          }
          card.clear();
        } else if (!savedCard) {
          // Card chosen but nothing entered and no card on file: nothing to put on file.
          setPaymentError(intl.formatMessage({ id: 'checkout.card.invalid' }));
          return false;
        }
        if (defaultPaymentMethod !== PaymentMethodType.CREDIT_CARD) {
          await dispatch(fromPaymentMethod.doUpdateDefaultPaymentMethod({
            defaultPaymentMethod: PaymentMethodType.CREDIT_CARD,
          })).then(unwrapResult);
        }
      }
      setEditingPayment(false);
      return true;
    } catch (e: any) {
      setPaymentError(e?.message
        || intl.formatMessage({ id: 'page.order.confirmation.v2.payment.method.save.error' }));
      return false;
    } finally {
      setSavingPayment(false);
    }
  };

  const handleEditBilling = () => setEditingBilling(true);
  const handleCancelBilling = () => {
    billingRef.current?.discard();
    setEditingBilling(false);
  };
  /** Validates and writes the billing form; the outcome tells whether the page must re-price. */
  const saveBilling = async (): Promise<BillingSaveOutcome> => {
    setSavingBilling(true);
    try {
      const outcome = await billingRef.current?.validateAndSave()
        ?? { ok: true, saved: false };
      if (outcome.ok) setEditingBilling(false);
      return outcome;
    } finally {
      setSavingBilling(false);
    }
  };

  const paymentDirty = () => editingPayment
    && (selectedPaymentMethod !== savedMethod || (isCard && !!cardRef.current?.isDirty()));
  const billingDirty = () => editingBilling && !!billingRef.current?.isDirty();

  useImperativeHandle(ref, () => ({
    validateAndSaveBillingInformation: () => (
      billingRef.current?.validateAndSave() ?? Promise.resolve({ ok: true, saved: false })
    ),
    isEnteringNewCard: () => cardRef.current?.isEnteringNewCard() ?? false,
    createInlinePaymentMethod: (billingDetails: InlineCardBillingDetails) => (
      cardRef.current
        ? cardRef.current.createPaymentMethod(billingDetails)
        : Promise.reject(new Error(intl.formatMessage({ id: 'checkout.card.not.ready' })))
    ),
    isEditing: () => editingPayment || editingBilling,
    getUnsavedEdits: () => {
      const payment = paymentDirty();
      const billing = billingDirty();
      if (payment && billing) return 'both';
      if (payment) return 'payment';
      if (billing) return 'billing';
      return undefined;
    },
    discardEdits: () => {
      if (editingPayment) handleCancelPayment();
      if (editingBilling) handleCancelBilling();
    },
    saveEdits: async () => {
      if (editingPayment && !(await savePayment())) return { ok: false };
      if (editingBilling) {
        const billing = await saveBilling();
        return { ok: billing.ok, billing };
      }
      return { ok: true };
    },
  }));

  const renderHeaderActions = (
    saved: boolean,
    editing: boolean,
    saving: boolean,
    ids: { edit: string; cancel: string; save: string },
    onEdit: () => void,
    onCancel: () => void,
    onSave: () => void,
  ) => {
    if (saved && !editing) {
      return (
        <button type="button" id={ids.edit} className={classes.actionLink} onClick={onEdit}>
          <FormattedMessage id="page.order.confirmation.v2.edit" />
        </button>
      );
    }
    if (editing) {
      return (
        <Box className={classes.headerActions}>
          <button type="button" id={ids.cancel} className={classes.ghostButton} onClick={onCancel} disabled={saving}>
            <FormattedMessage id="page.order.confirmation.v2.cancel" />
          </button>
          <button
            type="button"
            id={ids.save}
            className={classes.primaryButton}
            onClick={onSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving && <CircularProgress size={14} sx={{ color: 'currentColor' }} />}
            <FormattedMessage id="page.order.confirmation.v2.save" />
          </button>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box id="payment-billing-section" className={classes.root}>
      <Box className={`${classes.sectionHeader} ${classes.paymentHeader}`}>
        <Typography component="h2" className={classes.heading}>
          <FormattedMessage id="page.order.confirmation.v2.payment.method" />
        </Typography>
        {renderHeaderActions(
          !!savedKind,
          editingPayment,
          savingPayment,
          { edit: 'payment-method-change', cancel: 'payment-method-cancel', save: 'payment-method-save' },
          handleEditPayment,
          handleCancelPayment,
          () => { savePayment(); },
        )}
      </Box>

      {showSavedPayment && savedKind === 'bank' && (
        <Box id="payment-method-saved-bank">
          <Box className={classes.savedBank}>
            <Box component="span" className={classes.savedBankIcon}><BankIcon /></Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="span" className={classes.savedBankTitle}>
                <FormattedMessage id="page.order.confirmation.v2.payment.method.saved.bank" />
              </Typography>
              <Typography component="span" className={classes.savedBankHint}>
                <FormattedMessage id="page.order.confirmation.v2.payment.method.saved.bank.hint" />
              </Typography>
            </Box>
            <Box component="span" className={classes.defaultBadge}>
              <FormattedMessage id="page.order.confirmation.v2.card.saved.default" />
            </Box>
          </Box>
          <Box className={classes.note}>
            <Box component="span" sx={{ display: 'inline-flex', color: '#0f8461' }}><LockIcon /></Box>
            <Box component="span">
              <FormattedMessage id="page.order.confirmation.v2.payment.method.saved.bank.note" />
            </Box>
          </Box>
        </Box>
      )}

      {!showSavedPayment && (
        <PaymentMethodCards
          compact
          hideTitle
          showBankTransfer={!bankTransferUnavailable}
          handleSelectPaymentMethod={handleSelectPaymentMethod}
          lockedTo={lockedMethod}
        />
      )}
      {inlineCardEnabled && (showSavedCardBlock || showCardElements) && (
        <Box className={showSavedCardBlock ? undefined : classes.cardForm}>
          <InlineCardForm ref={cardRef} accountId={accountId} changingCard={editingPayment} />
        </Box>
      )}
      {paymentError && (
        <Typography id="payment-method-error" role="alert" className={classes.errorText}>
          {paymentError}
        </Typography>
      )}

      <Box className={`${classes.sectionHeader} ${classes.billingHeader}`}>
        <Box className={classes.headingGroup}>
          <Typography component="h2" className={classes.heading}>
            <FormattedMessage id="page.order.confirmation.v2.billing.title" />
          </Typography>
          <Typography component="p" className={classes.subheading}>
            <FormattedMessage id="page.order.confirmation.v2.billing.subtitle" />
          </Typography>
        </Box>
        {renderHeaderActions(
          hasSavedBilling,
          editingBilling,
          savingBilling,
          { edit: 'billing-edit', cancel: 'billing-cancel', save: 'billing-save' },
          handleEditBilling,
          handleCancelBilling,
          () => { saveBilling(); },
        )}
      </Box>
      <BillingDetailsForm
        ref={billingRef}
        accountId={accountId}
        summary={showBillingSummary}
        editingSaved={hasSavedBilling && editingBilling}
      />
    </Box>
  );
});
